import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, MapPin, Search, Star, Mail, Phone, Globe, Lock, Wrench, Filter,
} from "lucide-react";
import { toast } from "sonner";

export default function Technicians() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postal, setPostal] = useState("");
  const [dept, setDept] = useState("");
  const [brand, setBrand] = useState("");

  useEffect(() => {
    api.get("/technicians/meta/brands").then(({ data }) => setBrands(data.brands || []));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const p = new URLSearchParams();
      if (postal.trim()) p.set("postal_code", postal.trim());
      if (dept.trim()) p.set("department", dept.trim());
      if (brand) p.set("brand", brand);
      api.get(`/technicians?${p.toString()}`)
        .then(({ data }) => setItems(data.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [postal, dept, brand]);

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="technicians-page">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-lg bg-primary/10 text-primary">
          <MapPin className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Annuaire</p>
          <h1 className="mt-0.5 font-display text-3xl lg:text-4xl font-black tracking-tight">
            Techniciens spa près de chez vous
          </h1>
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">
        Trouvez un installateur ou technicien qualifié pour intervenir sur votre spa —
        filtres par marque, code postal et département.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={postal}
            onChange={(e) => setPostal(e.target.value)}
            placeholder="Code postal"
            className="pl-10 h-11"
            data-testid="tech-postal-filter"
            inputMode="numeric"
            maxLength={5}
          />
        </div>
        <div>
          <Input
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            placeholder="Département (ex. 13)"
            className="h-11"
            data-testid="tech-dept-filter"
            inputMode="numeric"
            maxLength={3}
          />
        </div>
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="tech-brand-filter"
          className="h-11 rounded-md border border-black/10 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize"
        >
          <option value="">Toutes les marques</option>
          {brands.map((b) => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>
        <Button asChild variant="outline" className="rounded-md h-11" data-testid="tech-become-btn">
          <Link to="/technicians/register"><Wrench className="h-4 w-4 mr-2" />Devenir technicien référencé</Link>
        </Button>
      </div>

      {!user && (
        <Card className="mt-4 p-4 border-amber-200 bg-amber-50/50 flex items-center gap-3">
          <Lock className="h-4 w-4 text-amber-700 shrink-0" />
          <p className="text-sm text-amber-900 leading-relaxed">
            <Link to="/register" className="font-semibold underline">Créez un compte gratuit</Link>{" "}
            pour afficher les coordonnées (email + téléphone) de chaque technicien.
          </p>
        </Card>
      )}

      <div className="mt-8" data-testid="tech-list">
        {loading ? (
          <div className="py-16 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="p-10 border-black/10 text-center text-sm text-muted-foreground">
            Aucun technicien trouvé pour ces filtres. Élargissez votre recherche ou{" "}
            <Link to="/technicians/register" className="text-primary font-semibold underline">
              référencez-vous
            </Link>.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((t) => <TechCard key={t.id} t={t} showContact={!!user} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function TechCard({ t, showContact }) {
  return (
    <Card className="p-5 border-black/10" data-testid={`tech-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {t.logo_url ? (
            <img src={t.logo_url} alt={t.company_name} className="h-11 w-11 rounded-lg object-cover" />
          ) : (
            <span className="grid place-items-center h-11 w-11 rounded-lg bg-primary/10 text-primary font-black">
              {t.company_name[0]?.toUpperCase()}
            </span>
          )}
          <div>
            <h3 className="font-display font-semibold leading-tight">{t.company_name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t.city} · {t.postal_code}{t.department && ` · ${t.department}`}
            </p>
          </div>
        </div>
        {t.featured && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            <Star className="h-3 w-3" /> Recommandé
          </span>
        )}
      </div>

      {t.description && (
        <p className="mt-3 text-sm text-foreground leading-relaxed line-clamp-3">
          {t.description}
        </p>
      )}

      {t.brands_supported?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {t.brands_supported.map((b) => (
            <span key={b} className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] uppercase tracking-wider">
              {b}
            </span>
          ))}
        </div>
      )}

      {t.certifications && (
        <p className="mt-3 text-xs text-muted-foreground italic">{t.certifications}</p>
      )}

      <div className="mt-4 pt-4 border-t border-black/5 text-xs flex items-center justify-between">
        <span className="text-muted-foreground">
          Rayon : {t.service_radius_km} km
        </span>
        {showContact ? (
          <div className="flex items-center gap-2" data-testid={`tech-contact-${t.id}`}>
            {t.phone && (
              <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1 text-primary hover:underline" title="Téléphone">
                <Phone className="h-3.5 w-3.5" /> {t.phone}
              </a>
            )}
            {t.email && (
              <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1 text-primary hover:underline" title="Email">
                <Mail className="h-3.5 w-3.5" />
              </a>
            )}
            {t.website && (
              <a href={t.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" title="Site web">
                <Globe className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground italic">Contact réservé aux membres</span>
        )}
      </div>
    </Card>
  );
}
